import React, { useMemo } from "react";
import { Box, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import Card from "components/card/Card.js";
import LineChart from "components/charts/LineChart";
import { GOLD_TEXT } from "constants/brandColors";
import REPORT_TIMEZONE from "constants/reportTimezone";

// Buckets are truncated in REPORT_TIMEZONE server-side, so label them in that
// same zone — any other zone would show e.g. Sep 28 for a Sep 29 bucket.
function formatBucket(bucket, interval) {
  const date = new Date(bucket);
  const timeZone = REPORT_TIMEZONE;
  if (interval === "hour") return date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false, timeZone });
  if (interval === "month") return date.toLocaleDateString(undefined, { month: "short", year: "numeric", timeZone });
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone });
}

// `rows` is /reports/news/publishing-trend's data: [{bucket, count, views}],
// already ordered ascending by the server. Rendered as a dual-axis line
// chart since article count and total views are on very different scales.
export default function PublishingTrendChart({ rows, interval }) {
  const { t } = useTranslation();

  const { categories, chartData } = useMemo(() => {
    const safeRows = rows || [];
    return {
      categories: safeRows.map((r) => formatBucket(r.bucket, interval)),
      chartData: [
        { name: t("Articles Published"), data: safeRows.map((r) => r.count) },
        { name: t("Views"), data: safeRows.map((r) => r.views) },
      ],
    };
  }, [rows, interval, t]);

  const chartOptions = useMemo(
    () => ({
      chart: { toolbar: { show: false } },
      colors: ["#C7B45E", "#4318FF"],
      stroke: { curve: "smooth", width: 3 },
      markers: { size: 4 },
      dataLabels: { enabled: false },
      tooltip: { theme: "light" },
      legend: { show: true, position: "top" },
      xaxis: {
        categories,
        labels: { style: { colors: "#A3AED0", fontSize: "12px" } },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: [
        { title: { text: t("Articles Published") }, labels: { style: { colors: "#A3AED0" } } },
        { opposite: true, title: { text: t("Views") }, labels: { style: { colors: "#A3AED0" } } },
      ],
      grid: { strokeDashArray: 5 },
    }),
    [categories, t]
  );

  return (
    <Card w="100%" p="20px">
      <Text color={GOLD_TEXT} fontSize="xl" fontWeight="700" mb="10px">
        {t("Publishing Trend")}
      </Text>
      {rows?.length ? (
        <Box h="320px">
          <LineChart chartData={chartData} chartOptions={chartOptions} />
        </Box>
      ) : (
        <Text color="gray.500" textAlign="center" py={10}>{t("No data found")}</Text>
      )}
    </Card>
  );
}
